import { api, ApiError } from "../api.js";
import { el } from "./dom.js";
import { gamesPanel } from "./games.js";
import { RANDOM_MAP_ID, type MatchmadeMode } from "@webolo/shared";

// Primary lobby surface. Replaces the old room-list. Each tile is a single
// big button that drives one user intent:
//   FREE FOR ALL → matchmaker (FFA queue)
//   TEAMS        → matchmaker (Teams queue)
//   SIEGE        → existing solo-on-demand siege room
//   CUSTOM GAME  → desktop-only; expands the legacy create/join panel below
//                  the tiles. SIEGE is hidden from that panel (it already
//                  has its own dedicated tile).
//
// The Custom Game tile is hidden on mobile (no room creation flow on small
// screens). The other three render across the available width.
export function tilesPanel(opts: {
  onMatchmake: (mode: MatchmadeMode) => void;
  onPlay: (gameId: string, team: number) => void;
  showCustomGame: boolean;
}): HTMLElement {
  const errorEl = el("div", { class: "lcard-error" });

  // The Custom Game expansion. We mount the legacy gamesPanel lazily on the
  // first toggle so the lobby's initial render stays cheap (the gamesPanel
  // kicks off a /api/games refresh interval, a map list fetch, and turbo /
  // siege config requests; not free).
  let customPanelHost: HTMLElement | null = null;
  let customPanelExpanded = false;

  async function onSiege(): Promise<void> {
    errorEl.textContent = "";
    try {
      const res = await api.createGame({
        name: "Siege Challenge",
        style: "siege",
        solo: true,
        mapId: RANDOM_MAP_ID,
      });
      // Solo siege has its own prep window built into the mode — skip the
      // Match Lobby and drop straight into the live game.
      opts.onPlay(res.id, 1);
    } catch (e) {
      errorEl.textContent = e instanceof ApiError && e.message === "siege_disabled"
        ? "Siege is currently disabled."
        : "Failed to start the siege challenge.";
    }
  }

  const tiles: HTMLElement[] = [
    tile({
      label: "FREE FOR ALL",
      subtitle: "Quick match · everyone for themselves",
      cls: "tile-ffa",
      onClick: () => opts.onMatchmake("ffa"),
    }),
    // SUPER FFA is the FFA queue with Turbo physics enabled — same join flow,
    // faster pace. Future paid power-ups will hang off this queue too.
    tile({
      label: "SUPER FREE FOR ALL",
      subtitle: "Fast-paced FFA — everyone for themselves",
      cls: "tile-super",
      onClick: () => opts.onMatchmake("super_ffa"),
    }),
    tile({
      label: "TEAMS",
      subtitle: "Two-team battle · auto-balanced",
      cls: "tile-teams",
      onClick: () => opts.onMatchmake("teams"),
    }),
    tile({
      label: "SIEGE",
      subtitle: "Solo wave survival",
      cls: "tile-siege",
      onClick: () => void onSiege(),
    }),
  ];

  // BATTLE ROYALE — 16-tank last-one-standing with a shrinking fog. Same
  // matchmade Match Lobby flow as FREE FOR ALL (skin/colour picking, bot fill).
  // Hidden until the admin gate confirms it's enabled, mirroring TURBO/SIEGE.
  const brTile = tile({
    label: "BATTLE ROYALE",
    subtitle: "16 tanks · shrinking fog · last one wins",
    cls: "tile-battle-royale hidden",
    onClick: () => opts.onMatchmake("battle_royale"),
  });
  tiles.push(brTile);
  void api.battleRoyaleConfig()
    .then(({ enabled }) => { if (enabled) brTile.classList.remove("hidden"); })
    .catch(() => { /* leave hidden on error */ });

  const customTile = opts.showCustomGame
    ? tile({
        label: "CUSTOM GAME",
        subtitle: "Create or join a specific room",
        cls: "tile-custom",
        onClick: () => toggleCustom(),
      })
    : null;
  if (customTile) tiles.push(customTile);

  const customSlot = el("div", { class: "tiles-custom-slot" });

  function toggleCustom(): void {
    customPanelExpanded = !customPanelExpanded;
    if (customTile) customTile.classList.toggle("tile-active", customPanelExpanded);
    if (customPanelExpanded) {
      if (!customPanelHost) {
        // Mount lazily and keep the same DOM node across collapses so the
        // games-list refresh interval stays warm.
        customPanelHost = gamesPanel(opts.onPlay, { omitSiege: true });
      }
      customSlot.replaceChildren(customPanelHost);
    } else {
      customSlot.replaceChildren();
    }
  }

  return el(
    "section",
    { class: "lcard lcard-tiles" },
    el(
      "header",
      { class: "lcard-head" },
      el("span", { class: "lcard-title" }, "PLAY"),
    ),
    el(
      "div",
      { class: "lcard-body" },
      errorEl,
      el("div", { class: "tiles-grid" }, ...tiles),
      customSlot,
    ),
  );
}

function tile(opts: {
  label: string;
  subtitle: string;
  cls: string;
  onClick: () => void;
}): HTMLElement {
  return el(
    "button",
    { class: `lobby-tile ${opts.cls}`, type: "button", onclick: opts.onClick },
    el("span", { class: "lobby-tile-label" }, opts.label),
    el("span", { class: "lobby-tile-sub" }, opts.subtitle),
  );
}
